import React from 'react';
import { Paper, Typography, Box, Divider, Stack, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { useGetOrderTimeline } from '../../../hooks/orders/useGetOrderTimeline';
import { ORDER_STATUS_META } from '../Table/OrderStatusBadge';

const getStatusLabel = (status, isArabic) => {
    if (!status) return '-';
    const meta = ORDER_STATUS_META[status];
    if (!meta) return status;
    return isArabic ? meta.labelAr : meta.labelEn;
};

const getStatusColor = (status) => ORDER_STATUS_META[status]?.color || 'default';

const OrderTimeline = ({ orderId }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { events, isLoading } = useGetOrderTimeline(orderId, { limit: 100 });

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                mb: 3,
            }}
        >
            <Typography variant="h6" fontWeight={700} mb={2}>
                {isArabic ? 'سجل الأحداث' : 'Timeline'}
            </Typography>

            {isLoading ? (
                <Typography variant="body2" color="text.secondary">
                    {isArabic ? 'جاري التحميل...' : 'Loading...'}
                </Typography>
            ) : events.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {isArabic ? 'لا توجد أحداث' : 'No events yet'}
                </Typography>
            ) : (
                <Stack divider={<Divider flexItem />} spacing={1.5}>
                    {events.map((ev) => {
                        const isStatusChange = ev.event === 'status_changed';
                        const toStatus = ev.payload?.to;
                        const fromStatus = ev.payload?.from;
                        const comment = ev.payload?.comment;
                        const trackingNumber = ev.payload?.tracking_number;

                        const fallbackLabel =
                            ev.event_label?.[i18n.language] || ev.event_label?.en || ev.event;

                        return (
                            <Box key={ev.id} sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap">
                                    {isStatusChange && toStatus ? (
                                        <>
                                            <Chip
                                                size="small"
                                                label={getStatusLabel(toStatus, isArabic)}
                                                color={getStatusColor(toStatus)}
                                                variant="tonal"
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                            {fromStatus && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {isArabic
                                                        ? `من ${getStatusLabel(fromStatus, true)}`
                                                        : `from ${getStatusLabel(fromStatus, false)}`}
                                                </Typography>
                                            )}
                                        </>
                                    ) : (
                                        <Chip
                                            size="small"
                                            label={fallbackLabel}
                                            color="primary"
                                            variant="tonal"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    )}
                                    {ev.actor?.name && (
                                        <Typography variant="caption" color="text.secondary">
                                            {ev.actor.type === 'admin'
                                                ? (isArabic ? 'بواسطة' : 'by')
                                                : ''}{' '}
                                            {ev.actor.name}
                                        </Typography>
                                    )}
                                </Stack>

                                {isStatusChange ? (
                                    <>
                                        {trackingNumber && toStatus === 'shipped' && (
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                {isArabic ? 'رقم التتبع: ' : 'Tracking #: '}
                                                <Box component="span" sx={{ fontWeight: 600 }}>
                                                    {trackingNumber}
                                                </Box>
                                            </Typography>
                                        )}
                                        {comment && (
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                {isArabic ? 'ملاحظة: ' : 'Note: '}
                                                {comment}
                                            </Typography>
                                        )}
                                    </>
                                ) : (
                                    ev.description && (
                                        <Typography variant="body2">{ev.description}</Typography>
                                    )
                                )}

                                <Typography variant="caption" color="text.secondary">
                                    {dayjs(ev.created_at).format('YYYY-MM-DD HH:mm')}
                                </Typography>
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </Paper>
    );
};

export default OrderTimeline;
